import React, { useCallback, useEffect, useState } from 'react';
import { Activity, Server, Globe, AlertCircle } from '../../ui/icons';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Colors from '../../../constants/Colors';
import { opsInfrastructureApi, OpsInfraMetricsResponse } from '../../../services/api';

type TimeRange = 5 | 15 | 30 | 60;

const RANGE_OPTIONS: TimeRange[] = [5, 15, 30, 60];
const POLL_INTERVAL_MS = 60000;

export default function InfrastructurePanel() {
  const [range, setRange] = useState<TimeRange>(5);
  const [metrics, setMetrics] = useState<OpsInfraMetricsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchMetrics = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    const response = await opsInfrastructureApi.getMetrics(range);
    if (response.success && response.data?.success) {
      setError(null);
      setMetrics(response.data);
      setLastUpdated(new Date());
    } else {
      setError(response.error || response.data?.error || 'Failed to load infrastructure metrics');
    }
    if (showSpinner) setLoading(false);
  }, [range]);

  useEffect(() => {
    fetchMetrics(true);
  }, [fetchMetrics]);

  useEffect(() => {
    const timer = setInterval(() => fetchMetrics(false), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchMetrics]);

  return (
    <div className="section">
      <h2 className="section-title">
        <Activity size={24} />
        Infrastructure
      </h2>

      <Card
        title="Platform health"
        description="Lambda, ECS, DynamoDB, CloudFront — CloudWatch metrics for the selected time window"
        headerAction={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '3px', background: Colors.background, borderRadius: '999px', padding: '3px' }}>
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRange(r)}
                  style={{
                    height: '30px', padding: '0 12px', borderRadius: '999px', border: 0, cursor: 'pointer',
                    fontSize: '12px', fontWeight: 700,
                    background: range === r ? '#FFFFFF' : 'transparent',
                    color: range === r ? Colors.text : Colors.textLight,
                    boxShadow: range === r ? '0 1px 3px rgba(21, 41, 92, 0.16)' : 'none',
                  }}
                >
                  {r}m
                </button>
              ))}
            </div>
            <Button onClick={() => fetchMetrics(true)} loading={loading} className="btn-small btn-primary">
              Refresh
            </Button>
          </div>
        }
      >
        {error && (
          <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
            <AlertCircle size={20} />
            <div>{error}</div>
          </div>
        )}

        {lastUpdated && (
          <div style={{ fontSize: '12px', color: Colors.textLight, marginBottom: '14px' }}>
            Last updated {lastUpdated.toLocaleTimeString()}
          </div>
        )}

        {/* ECS + CloudFront summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px', marginBottom: '20px' }}>
          <div className="card" style={{ boxShadow: 'none', border: '1px solid rgba(var(--color-border-rgb), 0.16)', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Server size={16} style={{ color: Colors.textLight }} />
              <span style={{ fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: Colors.textLight }}>ECS Cluster</span>
            </div>
            {metrics?.ecs ? (
              <>
                <div style={{ fontFamily: "'SF Mono', monospace", fontSize: '12.5px', color: Colors.textLight }}>{metrics.ecs.cluster}</div>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: Colors.textLight }}>Running tasks</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: Colors.text }}>{metrics.ecs.running_tasks}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: Colors.textLight }}>CPU</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: Colors.text }}>{metrics.ecs.cpu_percent}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: Colors.textLight }}>Memory</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: Colors.text }}>{metrics.ecs.memory_percent}%</div>
                  </div>
                </div>
              </>
            ) : (
              <span style={{ fontSize: '13px', color: Colors.inactive }}>No data</span>
            )}
          </div>

          <div className="card" style={{ boxShadow: 'none', border: '1px solid rgba(var(--color-border-rgb), 0.16)', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Globe size={16} style={{ color: Colors.textLight }} />
              <span style={{ fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: Colors.textLight }}>CloudFront</span>
            </div>
            {metrics?.cloudfront ? (
              <>
                <div style={{ fontFamily: "'SF Mono', monospace", fontSize: '12.5px', color: Colors.textLight }}>{metrics.cloudfront.distribution_id}</div>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: Colors.textLight }}>Requests</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: Colors.text }}>{metrics.cloudfront.requests}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: Colors.textLight }}>4xx</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: metrics.cloudfront.error_4xx_pct > 1 ? Colors.error : Colors.text }}>{metrics.cloudfront.error_4xx_pct}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: Colors.textLight }}>5xx</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: metrics.cloudfront.error_5xx_pct > 0.5 ? Colors.error : Colors.text }}>{metrics.cloudfront.error_5xx_pct}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: Colors.textLight }}>Cache hit</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: metrics.cloudfront.cache_hit_pct > 80 ? Colors.success : '#FF9500' }}>{metrics.cloudfront.cache_hit_pct}%</div>
                  </div>
                </div>
              </>
            ) : (
              <span style={{ fontSize: '13px', color: Colors.inactive }}>No data</span>
            )}
          </div>
        </div>

        {/* Lambda table */}
        {metrics && metrics.lambda.length > 0 && (
          <>
            <h3 className="card-title" style={{ fontSize: '15px', marginBottom: '12px' }}>Lambda functions</h3>
            <div className="table-scroll-wrap" style={{ border: '1px solid rgba(var(--color-border-rgb), 0.16)', borderRadius: '8px', overflow: 'auto', marginBottom: '20px' }}>
              <table className="table" style={{ marginBottom: 0, minWidth: '620px' }}>
                <colgroup>
                  <col style={{ width: '34%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Function</th>
                    <th>Invocations</th>
                    <th>Errors</th>
                    <th>p95</th>
                    <th>Throttles</th>
                    <th>Error %</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.lambda.map((l) => (
                    <tr key={l.function_name}>
                      <td style={{ fontFamily: "'SF Mono', monospace" }}>{l.function_name}</td>
                      <td style={{ fontWeight: 600 }}>{l.invocations}</td>
                      <td style={{ fontWeight: 600, color: l.errors > 0 ? Colors.error : Colors.success }}>{l.errors}</td>
                      <td style={{ fontFamily: "'SF Mono', monospace" }}>{l.duration_p95_ms} ms</td>
                      <td style={{ color: l.throttles > 0 ? Colors.error : Colors.text }}>{l.throttles}</td>
                      <td style={{ fontWeight: 600, color: l.error_rate > 5 ? Colors.error : l.error_rate > 0 ? '#FF9500' : Colors.success }}>{l.error_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* DynamoDB table */}
        {metrics && metrics.dynamodb.length > 0 && (
          <>
            <h3 className="card-title" style={{ fontSize: '15px', marginBottom: '12px' }}>DynamoDB tables</h3>
            <div className="table-scroll-wrap" style={{ border: '1px solid rgba(var(--color-border-rgb), 0.16)', borderRadius: '8px', overflow: 'auto' }}>
              <table className="table" style={{ marginBottom: 0, minWidth: '480px' }}>
                <colgroup>
                  <col style={{ width: '46%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '18%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Table</th>
                    <th>Reads</th>
                    <th>Writes</th>
                    <th>Throttles</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.dynamodb.map((t) => (
                    <tr key={t.table_name}>
                      <td style={{ fontFamily: "'SF Mono', monospace" }}>{t.table_name}</td>
                      <td>{t.reads}</td>
                      <td>{t.writes}</td>
                      <td style={{ color: t.throttles > 0 ? Colors.error : Colors.text }}>{t.throttles}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
